import {workspaceFileUrl} from '../../src/workspace-file-address';

type MarkdownNode = {type: string; value?: string; url?: string; children?: MarkdownNode[]};
export type WorkspaceFileLinkOptions = {accountId: string | null; publicOrigin: string; basePath: string};

/** A link to an account file, never a public application address. The API owns file access. */
export function workspaceFileAddress(path: string, options: WorkspaceFileLinkOptions): string | null {
  if (!options.accountId || !path.startsWith('/workspace/')) return null;
  let decoded: string;
  try {decoded = decodeURIComponent(path);} catch {return null;}
  if (!/^\/workspace\/(?:projects|documents)\//.test(decoded) || /[\u0000-\u001f\u007f\\?#;|<>]/.test(decoded)
    || /\s\/workspace\//.test(decoded) || decoded.includes('$(')) return null;
  const parts = decoded.slice('/workspace/'.length).split('/');
  if (parts.some(part => !part || part === '.' || part === '..')) return null;
  // Directory references stay text. The server decides which file types it serves.
  const name = parts.at(-1)!;
  if (!/\.[^./\s]+$/.test(name)) return null;
  const origin = new URL(options.publicOrigin);
  if (origin.protocol !== 'https:' || origin.origin !== options.publicOrigin) return null;
  return workspaceFileUrl(options.accountId, parts.join('/'), origin.origin, options.basePath);
}

function fileLabel(path: string): string {
  const name = path.slice(path.lastIndexOf('/') + 1);
  try {return decodeURIComponent(name);} catch {return name;}
}

function textLinks(value: string, options: WorkspaceFileLinkOptions): MarkdownNode[] {
  const nodes: MarkdownNode[] = [];
  const pattern = /\/workspace\/(?:projects|documents)\/[^\s<>`"']+/g;
  let cursor = 0;
  for (const match of value.matchAll(pattern)) {
    const start = match.index;
    // Do not match the path portion of another address or a larger code token.
    if (start > 0 && !/[\s([{]/.test(value[start - 1]!)) continue;
    const path = match[0].replace(/[),.;:!?\]}]+$/, '');
    const url = workspaceFileAddress(path, options);
    if (!url) continue;
    if (start > cursor) nodes.push({type: 'text', value: value.slice(cursor, start)});
    nodes.push({type: 'link', url, children: [{type: 'text', value: fileLabel(path)}]});
    cursor = start + path.length;
  }
  if (cursor === 0) return [{type: 'text', value}];
  if (cursor < value.length) nodes.push({type: 'text', value: value.slice(cursor)});
  return nodes;
}

/** Transform Markdown nodes so code blocks and inline commands remain unchanged. */
export function remarkWorkspaceFiles(options: WorkspaceFileLinkOptions) {
  return (tree: MarkdownNode): void => {
    function visit(parent: MarkdownNode): void {
      if (!parent.children || ['code', 'html', 'link', 'linkReference'].includes(parent.type)) return;
      parent.children = parent.children.flatMap(node => {
        if ((node.type === 'link' || node.type === 'image' || node.type === 'definition') && node.url) {
          const url = workspaceFileAddress(node.url, options);
          if (url) {
            const old = node.url;
            node.url = url;
            if (node.type === 'link' && node.children) node.children = node.children.map(child => child.type === 'text' && child.value === old
              ? {...child, value: fileLabel(old)} : child);
          }
          return [node];
        }
        if (node.type === 'inlineCode' && node.value) {
          const url = workspaceFileAddress(node.value, options);
          return url ? [{type: 'link', url, children: [{type: 'text', value: fileLabel(node.value)}]}] : [node];
        }
        if (node.type === 'text' && node.value) return textLinks(node.value, options);
        visit(node);
        return [node];
      });
    }
    visit(tree);
  };
}
